import React, { useState, useEffect } from "react";
import config from "../config/config";

const API_URL = config.API_URL;

function ManageItemModal({ item, userTable, onClose, onUpdate, allStartups }) {
  const [selectedStartupId, setSelectedStartupId] = useState(item?.startup_id || "");
  const [startupName, setStartupName] = useState(item?.name || "");
  const [gpsLatitude, setGpsLatitude] = useState("");
  const [gpsLongitude, setGpsLongitude] = useState("");

  useEffect(() => {
    if (!userTable && item) {
      // 팀 모드일 때 GPS 위치 초기화
      setStartupName(item.name || "");
      // GPS 정보가 있다면 파싱 (추후 백엔드에서 받아올 수 있음)
      // 현재는 빈 값으로 초기화
      setGpsLatitude("");
      setGpsLongitude("");
    }
  }, [item, userTable]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleDelete = async () => {
    const itemName = item.name;
    const confirmMessage = userTable
      ? `정말로 ${itemName} 사용자를 삭제하시겠습니까?`
      : `정말로 ${itemName} 팀을 삭제하시겠습니까?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const endpoint = userTable ? '/api/auth/deleteUser' : '/api/startup/deleteStartup';
      const bodyData = userTable
        ? { user_id: item.user_id }
        : { startup_id: item.startup_id };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(bodyData),
      });

      const result = await response.json();

      if (result.result) {
        if (onUpdate) {
          await onUpdate();
        }
        onClose();
        setTimeout(() => {
          alert(`${itemName}이(가) 삭제되었습니다.`);
        }, 100);
      } else {
        alert(result.message || '삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const handleMoveTeam = async () => {
    if (!selectedStartupId || selectedStartupId === item.startup_id) {
      alert('이동할 팀을 선택해주세요.');
      return;
    }

    const selectedStartup = allStartups.find(s => s.startup_id === parseInt(selectedStartupId));
    const confirmMessage = `${item.name} 사용자를 ${selectedStartup?.name} 팀으로 이동하시겠습니까?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/moveUserToStartup`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: item.user_id,
          new_startup_id: parseInt(selectedStartupId),
        }),
      });

      const result = await response.json();

      if (result.result) {
        if (onUpdate) {
          await onUpdate();
        }
        onClose();
        setTimeout(() => {
          alert(`${item.name} 사용자가 ${selectedStartup.name} 팀으로 이동되었습니다.`);
        }, 100);
      } else {
        alert(result.message || '팀 이동에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error moving user:', error);
      alert('팀 이동 중 오류가 발생했습니다.');
    }
  };

  const handleUpdateStartup = async () => {
    if (!startupName.trim()) {
      alert('팀 이름을 입력해주세요.');
      return;
    }

    const confirmMessage = `팀 정보를 수정하시겠습니까?`;
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/startup/updateStartupInfo`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          startup_id: item.startup_id,
          name: startupName,
          gps_latitude: gpsLatitude || null,
          gps_longitude: gpsLongitude || null,
        }),
      });

      const result = await response.json();

      if (result.result) {
        if (onUpdate) {
          await onUpdate();
        }
        onClose();
        setTimeout(() => {
          alert('팀 정보가 수정되었습니다.');
        }, 100);
      } else {
        alert(result.message || '팀 정보 수정에 실패했습니다.');
      }
    } catch (error) {
      console.error('Error updating startup:', error);
      alert('팀 정보 수정 중 오류가 발생했습니다.');
    }
  };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <div>
            <h3 style={styles.title}>{userTable ? '사용자 관리' : '팀 관리'}</h3>
            <p style={styles.subtitle}>{item?.name}</p>
          </div>
          <button style={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div style={styles.content}>
          {userTable ? (
            // 사용자 모드: 팀 이동
            <div style={styles.section}>
              <div style={styles.label}>
                <span style={styles.labelIcon}>🔄</span>
                <span style={styles.labelText}>팀 이동</span>
              </div>
              <select
                value={selectedStartupId}
                onChange={(e) => setSelectedStartupId(e.target.value)}
                style={styles.selectInput}
              >
                <option value="">팀 선택</option>
                {allStartups && allStartups
                  .filter(s => s.startup_id !== item.startup_id)
                  .map(startup => (
                    <option key={startup.startup_id} value={startup.startup_id}>
                      {startup.name}
                    </option>
                  ))}
              </select>
              <button style={styles.actionButton} onClick={handleMoveTeam}>
                팀 이동
              </button>
            </div>
          ) : (
            // 팀 모드: 팀 정보 수정
            <>
              <div style={styles.section}>
                <div style={styles.label}>
                  <span style={styles.labelIcon}>✏️</span>
                  <span style={styles.labelText}>팀 이름</span>
                </div>
                <input
                  type="text"
                  value={startupName}
                  onChange={(e) => setStartupName(e.target.value)}
                  style={styles.textInput}
                  placeholder="팀 이름을 입력하세요"
                />
              </div>

              <div style={styles.section}>
                <div style={styles.label}>
                  <span style={styles.labelIcon}>📍</span>
                  <span style={styles.labelText}>GPS 위치</span>
                </div>
                <div style={styles.gpsContainer}>
                  <input
                    type="text"
                    value={gpsLatitude}
                    onChange={(e) => setGpsLatitude(e.target.value)}
                    style={{...styles.textInput, marginBottom: '8px'}}
                    placeholder="위도 (Latitude)"
                  />
                  <input
                    type="text"
                    value={gpsLongitude}
                    onChange={(e) => setGpsLongitude(e.target.value)}
                    style={styles.textInput}
                    placeholder="경도 (Longitude)"
                  />
                </div>
              </div>

              <button style={styles.actionButton} onClick={handleUpdateStartup}>
                팀 정보 수정
              </button>
            </>
          )}

          {/* 삭제 섹션 - 공통 */}
          <div style={styles.dangerSection}>
            <div style={styles.label}>
              <span style={styles.labelIcon}>🗑️</span>
              <span style={styles.labelText}>
                {userTable ? '사용자 삭제' : '팀 삭제'}
              </span>
            </div>
            <p style={styles.warningText}>
              {userTable
                ? '삭제된 사용자는 출퇴근 기록에서 보이지 않게 됩니다.'
                : '삭제된 팀은 출퇴근 기록에서 보이지 않게 됩니다.'}
            </p>
            <button style={styles.deleteButton} onClick={handleDelete}>
              삭제
            </button>
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelButton} onClick={onClose}>
            닫기
          </button>
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modal: {
    backgroundColor: "#fff",
    borderRadius: "12px",
    width: "90%",
    maxWidth: "500px",
    maxHeight: "80vh",
    overflowY: "auto",
    boxShadow: "0 4px 24px rgba(0, 0, 0, 0.15)",
  },
  header: {
    padding: "20px 24px",
    borderBottom: "1px solid #E5E5E5",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  title: {
    margin: 0,
    fontSize: "18px",
    fontWeight: "600",
    color: "#232323",
    marginBottom: "4px",
  },
  subtitle: {
    margin: 0,
    fontSize: "14px",
    color: "#666",
  },
  closeButton: {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: "#999",
    cursor: "pointer",
    padding: "0",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    padding: "24px",
  },
  section: {
    marginBottom: "24px",
  },
  dangerSection: {
    marginTop: "32px",
    paddingTop: "24px",
    borderTop: "1px solid #E5E5E5",
  },
  label: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
  },
  labelIcon: {
    fontSize: "20px",
    marginRight: "8px",
  },
  labelText: {
    fontSize: "14px",
    fontWeight: "600",
    color: "#666",
  },
  textInput: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "2px solid #E0E0E0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  },
  selectInput: {
    width: "100%",
    padding: "12px",
    fontSize: "14px",
    border: "2px solid #E0E0E0",
    borderRadius: "8px",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
    marginBottom: "12px",
  },
  gpsContainer: {
    display: "flex",
    flexDirection: "column",
  },
  actionButton: {
    width: "100%",
    padding: "12px",
    backgroundColor: "#686769",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer",
    marginTop: "12px",
  },
  deleteButton: {
    width: "100%",
    padding: "12px",
    backgroundColor: "#FF4444",
    border: "none",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#fff",
    cursor: "pointer",
  },
  warningText: {
    fontSize: "12px",
    color: "#999",
    margin: "8px 0 12px 0",
  },
  footer: {
    padding: "16px 24px",
    borderTop: "1px solid #E5E5E5",
    display: "flex",
    justifyContent: "center",
  },
  cancelButton: {
    padding: "12px 32px",
    backgroundColor: "#F8F8F8",
    border: "1px solid #E0E0E0",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#666",
    cursor: "pointer",
  },
};

export default ManageItemModal;
